// skipcq: JS-W1028
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

function SavingSelectIcon({ className }) {
  return (
    <CircularProgress
      className={className}
      size={16}
      sx={{ mr: 2 }}
      aria-label="Saving style"
    />
  );
}

SavingSelectIcon.propTypes = {
  className: PropTypes.string,
};

function StyleTypeSelect({
  styleName,
  value,
  disabled,
  saving,
  onChange,
  typeOptions,
  label,
  compact = false,
  instanceKey,
}) {
  const labelText = label || styleName;
  const idSource = instanceKey || styleName;
  const labelId = `style-type-${String(idSource).replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <FormControl size="small" sx={{ minWidth: compact ? 160 : 240 }} disabled={disabled}>
      <InputLabel id={labelId}>{labelText}</InputLabel>
      <Select
        labelId={labelId}
        label={labelText}
        name={styleName}
        value={value}
        onChange={onChange}
        aria-busy={saving || undefined}
        IconComponent={saving ? SavingSelectIcon : undefined}
      >
        {typeOptions.map((typeRow) => (
          <MenuItem key={typeRow.id || typeRow.name} value={typeRow.name}>
            {typeRow.name}
          </MenuItem>
        ))}
      </Select>
      {saving ? <FormHelperText>Saving style…</FormHelperText> : null}
    </FormControl>
  );
}

StyleTypeSelect.propTypes = {
  styleName: PropTypes.string.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  saving: PropTypes.bool,
  onChange: PropTypes.func,
  typeOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
  })).isRequired,
  label: PropTypes.string,
  compact: PropTypes.bool,
  instanceKey: PropTypes.string,
};

export { StyleTypeSelect };

const DEFAULT_STYLE_TYPE_PROMPT = 'Assign a style type for unknown extracted styles. Mapped styles apply automatically.';

export default function BeerIngestionStyleTypeStrip({
  unknownStyles,
  disabled,
  onAssignStyleType,
  styleTypes,
  assigningStyleName,
  assigningStyleType,
  prompt = DEFAULT_STYLE_TYPE_PROMPT,
}) {
  if (!unknownStyles || unknownStyles.length === 0) {
    return null;
  }
  const typeOptions = (styleTypes || []).filter((typeRow) => typeRow?.name);
  const assignInFlight = Boolean(assigningStyleName);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {prompt}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {unknownStyles.map((styleName) => {
          const saving = assigningStyleName === styleName;
          return (
            <StyleTypeSelect
              key={styleName}
              styleName={styleName}
              value={saving ? (assigningStyleType || '') : ''}
              disabled={disabled || assignInFlight}
              saving={saving}
              onChange={onAssignStyleType || undefined}
              typeOptions={typeOptions}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

BeerIngestionStyleTypeStrip.propTypes = {
  unknownStyles: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool,
  onAssignStyleType: PropTypes.func,
  styleTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  assigningStyleName: PropTypes.string,
  assigningStyleType: PropTypes.string,
  prompt: PropTypes.string,
};
